import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  NOME_TIPO_SOCIETARIO,
  DESTINOS_DE_TRANSFORMACAO,
  nomeDe,
  type Ato,
  type Pagina,
  type Pessoa,
  type Sociedade,
} from '@social/shared';
import { PaginaShell } from '../componentes/pagina-shell';
import { Spinner } from '../componentes/carregando';
import { Aviso, Botao, Campo, Entrada, Selecao } from '../componentes/ui';
import { ErroApi } from '../lib/api';
import { api } from '../lib/sessao';
import { Secao, LinhaCheia } from './sociedade-nova-secao';
import { hojeISO } from './sociedade-nova-comum';
import {
  ATOS_ESPECIAIS,
  CAMINHO,
  NOME_ATO,
  O_QUE_FAZ,
  OPCOES_PODERES,
  corpoDoAto,
  oQueFalta,
  rascunhoVazio,
  type AtoEspecial,
  type RascunhoAto,
} from './ato-novo-comum';

/*
  Os quatro atos que não são o contrato.

  ## As seis perguntas (skill `social-desenho`)
  1. **Qual pergunta a pessoa traz?** "Preciso do documento de X para esta
     sociedade." Ela chega sabendo qual ato quer — o que ela não sabe, às
     vezes, é se "transformação" é o nome do que ela precisa.
  2. **Quantos registros?** Um ato. Sem lista, sem busca.
  3. **Resultado vs aparência?** Nada filtra nada.
  4. **Barra ou gaveta?** Não se aplica.
  5. **375px?** Campos numa coluna, como o resto do produto.
  6. **O que promete e não existe?** A transformação só oferece os cinco tipos
     que o gerador monta. S.A. e Comandita por Ações ficam de fora: elas
     exigem estatuto e ações, e oferecer o destino sem saber redigir o
     resultado produziria um instrumento apontando para um documento que
     nunca sai.

  ## Uma tela, quatro atos
  O tipo é a primeira pergunta, e os campos trocam conforme a resposta. Quatro
  rotas separadas obrigariam o contador a acertar o caminho antes de ver as
  opções — e "qual das quatro telas é a de distrato?" é uma pergunta que a
  interface não deveria fazer.
*/

export function AtoNovo(): JSX.Element {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const fila = useQueryClient();

  const [r, setR] = useState<RascunhoAto>(() => rascunhoVazio(hojeISO()));

  const sociedade = useQuery({
    queryKey: ['sociedade', id] as const,
    queryFn: () => api.get<Sociedade>(`/sociedades/${id}`),
  });

  const pessoasQuery = useQuery({
    queryKey: ['pessoas', 'consulta'] as const,
    queryFn: () => api.get<Pagina<Pessoa>>('/pessoas?tamanho=200'),
  });

  const s = sociedade.data;
  const todas = pessoasQuery.data?.itens ?? [];

  /*
    Sócios e administradores VIGENTES, com o nome já resolvido. Quem se retirou
    não aparece em lugar nenhum desta tela: não guarda livros, não preside
    reunião, não consta como presente.
  */
  const doQuadro = useMemo(() => {
    if (!s) return [];
    const ids = new Set<string>();
    for (const p of s.socios) if (!p.saida) ids.add(p.pessoaId);
    for (const a of s.administradores) if (!a.fim) ids.add(a.pessoaId);
    return todas.filter((p) => ids.has(p.id));
  }, [s, todas]);

  const mudar = (parcial: Partial<RascunhoAto>) =>
    setR((atual) => ({ ...atual, ...parcial }));

  const salvar = useMutation({
    mutationFn: () =>
      api.post<Ato>(`/sociedades/${id}/${CAMINHO[r.tipo]}`, corpoDoAto(r)),
    onSuccess: async () => {
      await fila.invalidateQueries({ queryKey: ['atos', id] });
      await fila.invalidateQueries({ queryKey: ['sociedade', id] });
      // Volta ao detalhe: quem acabou de abrir o ato quer conferir e gerar o
      // documento, que é o motivo de tê-lo aberto.
      navegar(`/sociedades/${id}`);
    },
  });

  const falta = oQueFalta(r);

  if (sociedade.isPending) {
    return (
      <PaginaShell titulo="Novo ato">
        <p className="text-tinta-500 flex items-center gap-2 text-sm">
          <Spinner /> Carregando a sociedade…
        </p>
      </PaginaShell>
    );
  }

  return (
    <PaginaShell
      titulo="Novo ato"
      subtitulo={`${s?.nomeEmpresarial ?? ''} · cadastro #${String(s?.codigo ?? '')}`}
      acoes={
        <Link
          to={`/sociedades/${id}`}
          className="border-tinta-100 bg-campo text-tinta-900 hover:border-marca-600 hover:bg-marca-600/10 inline-flex h-11 items-center gap-2 rounded-lg border px-3 text-sm whitespace-nowrap transition-colors"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Voltar
        </Link>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          salvar.mutate();
        }}
        className="flex flex-col gap-4"
        noValidate
      >
        {salvar.isError && <Aviso>{mensagemDoErro(salvar.error)}</Aviso>}

        <Secao titulo="O ato">
          <Campo rotulo="Tipo de ato" id="tipoAto" obrigatorio>
            <Selecao
              id="tipoAto"
              value={r.tipo}
              onChange={(e) => mudar({ tipo: e.target.value as AtoEspecial })}
            >
              {ATOS_ESPECIAIS.map((t) => (
                <option key={t} value={t}>
                  {NOME_ATO[t]}
                </option>
              ))}
            </Selecao>
          </Campo>

          <Campo
            rotulo="Data do instrumento"
            id="dataAto"
            obrigatorio
            dica="A data em que o ato foi assinado — é ela que vai no documento."
          >
            <Entrada
              id="dataAto"
              type="date"
              value={r.data}
              onChange={(e) => mudar({ data: e.target.value })}
            />
          </Campo>

          {/*
            O que o ato FAZ, junto do seletor. A diferença entre "alteração" e
            "transformação" não é óbvia para quem não vive de direito
            societário — e escolher errado só se descobre quando a Junta
            devolve.
          */}
          <LinhaCheia>
            <p className="text-tinta-500 text-sm">{O_QUE_FAZ[r.tipo]}</p>
          </LinhaCheia>
        </Secao>

        {r.tipo === 'DISTRATO' && (
          <CamposDistrato rascunho={r} mudar={mudar} pessoas={doQuadro} />
        )}
        {r.tipo === 'PROCURACAO' && (
          <CamposProcuracao rascunho={r} mudar={mudar} pessoas={todas} />
        )}
        {r.tipo === 'ATA' && <CamposAta rascunho={r} mudar={mudar} pessoas={doQuadro} />}
        {r.tipo === 'TRANSFORMACAO' && (
          <CamposTransformacao rascunho={r} mudar={mudar} tipoAtual={s?.tipo ?? ''} />
        )}

        <div className="border-tinta-100 flex flex-wrap items-center justify-end gap-3 border-t pt-4">
          {/*
            O que falta, por extenso, ao lado do botão. Botão desabilitado sem
            dizer o motivo é o pior estado de um formulário longo: a pessoa
            relê tudo procurando o campo.
          */}
          {falta && <span className="text-tinta-500 mr-auto text-sm">{falta}</span>}

          <Link
            to={`/sociedades/${id}`}
            className="text-tinta-700 hover:bg-marca-600/10 inline-flex h-11 items-center rounded-lg px-4 text-sm transition-colors"
          >
            Cancelar
          </Link>
          <Botao type="submit" disabled={falta !== null} carregando={salvar.isPending}>
            <Save className="size-4" aria-hidden />
            Gerar {NOME_ATO[r.tipo].toLowerCase()}
          </Botao>
        </div>
      </form>
    </PaginaShell>
  );
}

type Props = {
  rascunho: RascunhoAto;
  mudar: (p: Partial<RascunhoAto>) => void;
  pessoas: Pessoa[];
};

function CamposDistrato({ rascunho: r, mudar, pessoas }: Props): JSX.Element {
  return (
    <Secao
      titulo="Dissolução"
      descricao="Depois do distrato a sociedade está extinta, e nenhum outro ato pode ser praticado."
    >
      <LinhaCheia>
        <Campo
          rotulo="Motivo da dissolução"
          id="motivoDistrato"
          obrigatorio
          dica="Como sai no documento — em minúsculas, sem ponto final."
        >
          <Entrada
            id="motivoDistrato"
            value={r.motivo}
            placeholder="encerramento das atividades por decisão dos sócios"
            onChange={(e) => mudar({ motivo: e.target.value })}
          />
        </Campo>
      </LinhaCheia>

      <Campo
        rotulo="Responsável pelos livros"
        id="responsavelLivros"
        obrigatorio
        dica="A lei exige nomear quem guarda a escrituração (art. 1.194 CC)."
      >
        <Selecao
          id="responsavelLivros"
          value={r.responsavelLivrosId}
          onChange={(e) => mudar({ responsavelLivrosId: e.target.value })}
        >
          <option value="">Selecione…</option>
          {pessoas.map((p) => (
            <option key={p.id} value={p.id}>
              {nomeDe(p)}
            </option>
          ))}
        </Selecao>
      </Campo>

      <Campo
        rotulo="Acervo líquido (R$)"
        id="acervo"
        dica="Zero é comum: sociedade sem patrimônio a partilhar."
      >
        <Entrada
          id="acervo"
          inputMode="decimal"
          value={r.acervo}
          onChange={(e) => mudar({ acervo: e.target.value })}
        />
      </Campo>

      <LinhaCheia>
        <label className="text-tinta-700 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={r.temPassivo}
            onChange={(e) => mudar({ temPassivo: e.target.checked })}
          />
          Há passivo a descoberto — os sócios o assumem na proporção das quotas
        </label>
      </LinhaCheia>
    </Secao>
  );
}

function CamposProcuracao({ rascunho: r, mudar, pessoas }: Props): JSX.Element {
  const alternarPoder = (poder: (typeof OPCOES_PODERES)[number]['valor']) =>
    mudar({
      poderes: r.poderes.includes(poder)
        ? r.poderes.filter((p) => p !== poder)
        : [...r.poderes, poder],
    });

  return (
    <Secao
      titulo="Mandato"
      descricao="A sociedade outorga; o administrador assina por ela. O outorgado não precisa ser sócio."
    >
      <Campo rotulo="Outorgado" id="outorgado" obrigatorio>
        <Selecao
          id="outorgado"
          value={r.outorgadoId}
          onChange={(e) => mudar({ outorgadoId: e.target.value })}
        >
          <option value="">Selecione…</option>
          {/* A lista INTEIRA: procuração para advogado ou despachante é o caso
              mais comum, e nenhum dos dois é sócio. */}
          {pessoas.map((p) => (
            <option key={p.id} value={p.id}>
              {nomeDe(p)}
            </option>
          ))}
        </Selecao>
      </Campo>

      <Campo
        rotulo="Válida até"
        id="validaAte"
        dica="Vazio = prazo indeterminado, até revogação."
      >
        <Entrada
          id="validaAte"
          type="date"
          value={r.validaAte}
          onChange={(e) => mudar({ validaAte: e.target.value })}
        />
      </Campo>

      <LinhaCheia>
        <fieldset className="border-tinta-100 flex flex-col gap-2 rounded-lg border p-3">
          <legend className="text-tinta-700 px-1 text-sm font-medium">Poderes</legend>
          {OPCOES_PODERES.map((o) => (
            <label
              key={o.valor}
              className="text-tinta-700 flex items-start gap-2 text-sm"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={r.poderes.includes(o.valor)}
                onChange={() => alternarPoder(o.valor)}
              />
              {o.rotulo}
            </label>
          ))}
        </fieldset>
      </LinhaCheia>

      <LinhaCheia>
        <Campo
          rotulo="Finalidade específica"
          id="finalidade"
          dica="Opcional. Ex.: representar a sociedade no processo nº 123."
        >
          <Entrada
            id="finalidade"
            value={r.finalidade}
            onChange={(e) => mudar({ finalidade: e.target.value })}
          />
        </Campo>
      </LinhaCheia>

      <LinhaCheia>
        <label className="text-tinta-700 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={r.podeSubstabelecer}
            onChange={(e) => mudar({ podeSubstabelecer: e.target.checked })}
          />
          O procurador pode substabelecer
        </label>
      </LinhaCheia>
    </Secao>
  );
}

function CamposAta({ rascunho: r, mudar, pessoas }: Props): JSX.Element {
  const alternarPresente = (id: string) =>
    mudar({
      presentesIds: r.presentesIds.includes(id)
        ? r.presentesIds.filter((p) => p !== id)
        : [...r.presentesIds, id],
    });

  return (
    <Secao
      titulo="A reunião"
      descricao="O quórum é calculado das quotas de quem esteve presente — não se digita."
    >
      <Campo rotulo="Hora" id="horaAta" obrigatorio dica="HH:MM">
        <Entrada
          id="horaAta"
          value={r.hora}
          placeholder="14:30"
          onChange={(e) => mudar({ hora: e.target.value })}
        />
      </Campo>

      <Campo rotulo="Local" id="localAta" obrigatorio>
        <Entrada
          id="localAta"
          value={r.local}
          onChange={(e) => mudar({ local: e.target.value })}
        />
      </Campo>

      <LinhaCheia>
        <fieldset className="border-tinta-100 flex flex-col gap-2 rounded-lg border p-3">
          <legend className="text-tinta-700 px-1 text-sm font-medium">Presentes</legend>
          {pessoas.map((p) => (
            <label key={p.id} className="text-tinta-700 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={r.presentesIds.includes(p.id)}
                onChange={() => alternarPresente(p.id)}
              />
              {nomeDe(p)}
            </label>
          ))}
        </fieldset>
      </LinhaCheia>

      <Campo rotulo="Presidente" id="presidente" obrigatorio>
        <Selecao
          id="presidente"
          value={r.presidenteId}
          onChange={(e) => mudar({ presidenteId: e.target.value })}
        >
          <option value="">Selecione…</option>
          {pessoas.map((p) => (
            <option key={p.id} value={p.id}>
              {nomeDe(p)}
            </option>
          ))}
        </Selecao>
      </Campo>

      <Campo rotulo="Secretário" id="secretario" obrigatorio>
        <Selecao
          id="secretario"
          value={r.secretarioId}
          onChange={(e) => mudar({ secretarioId: e.target.value })}
        >
          <option value="">Selecione…</option>
          {pessoas.map((p) => (
            <option key={p.id} value={p.id}>
              {nomeDe(p)}
            </option>
          ))}
        </Selecao>
      </Campo>

      <LinhaCheia>
        <Campo
          rotulo="Ordem do dia"
          id="ordemDoDia"
          obrigatorio
          dica="Um item por linha."
        >
          <textarea
            id="ordemDoDia"
            rows={3}
            value={r.ordemDoDia}
            onChange={(e) => mudar({ ordemDoDia: e.target.value })}
            className="border-tinta-100 bg-campo text-tinta-900 placeholder:text-tinta-500 w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Campo>
      </LinhaCheia>

      <LinhaCheia>
        <Campo
          rotulo="Deliberações"
          id="deliberacoes"
          obrigatorio
          dica="Uma por linha, na ordem da pauta."
        >
          <textarea
            id="deliberacoes"
            rows={3}
            value={r.deliberacoes}
            onChange={(e) => mudar({ deliberacoes: e.target.value })}
            className="border-tinta-100 bg-campo text-tinta-900 placeholder:text-tinta-500 w-full rounded-lg border px-3 py-2 text-sm"
          />
        </Campo>
      </LinhaCheia>
    </Secao>
  );
}

function CamposTransformacao({
  rascunho: r,
  mudar,
  tipoAtual,
}: {
  rascunho: RascunhoAto;
  mudar: (p: Partial<RascunhoAto>) => void;
  tipoAtual: string;
}): JSX.Element {
  return (
    <Secao
      titulo="Transformação"
      descricao="A sociedade muda de tipo sem se dissolver: o CNPJ, os contratos e os processos continuam."
    >
      <Campo rotulo="Novo tipo societário" id="tipoNovo" obrigatorio>
        <Selecao
          id="tipoNovo"
          value={r.tipoNovo}
          onChange={(e) => mudar({ tipoNovo: e.target.value })}
        >
          <option value="">Selecione…</option>
          {/*
            Só os cinco que o gerador monta, e nunca o tipo ATUAL: transformar
            uma LTDA em LTDA é um instrumento que não muda nada, e a Junta o
            devolve. S.A. e Comandita por Ações ficam de fora até a fatia de
            ações e estatuto.
          */}
          {DESTINOS_DE_TRANSFORMACAO.filter((t) => t !== tipoAtual).map((t) => (
            <option key={t} value={t}>
              {NOME_TIPO_SOCIETARIO[t]}
            </option>
          ))}
        </Selecao>
      </Campo>

      <LinhaCheia>
        <Campo rotulo="Motivo" id="motivoTransformacao" obrigatorio>
          <Entrada
            id="motivoTransformacao"
            value={r.motivo}
            placeholder="adequação à natureza intelectual das atividades"
            onChange={(e) => mudar({ motivo: e.target.value })}
          />
        </Campo>
      </LinhaCheia>

      {/*
        O aviso da unanimidade. O art. 1.114 a exige, e sem ela o rito é outro
        — com direito de retirada e apuração de haveres, que este produto ainda
        não monta. Dizer isso aqui evita o instrumento assinado por quem não
        podia assiná-lo.
      */}
      <LinhaCheia>
        <p className="bg-alerta-100 text-alerta-600 rounded-lg px-4 py-3 text-sm">
          A transformação exige o consentimento de <strong>todos</strong> os sócios (art.
          1.114 CC). Ao gerar o instrumento, você declara que ele foi obtido.
        </p>
      </LinhaCheia>
    </Secao>
  );
}

function mensagemDoErro(erro: unknown): string {
  // A mensagem do servidor diz "esta sociedade já foi extinta por distrato" ou
  // nomeia a regra que reprovou — melhor que qualquer texto genérico daqui.
  if (erro instanceof ErroApi) return erro.message;
  return 'Não foi possível gerar o ato. Tente de novo.';
}
